import React, { useCallback, useEffect, useRef } from 'react';
import {
  FiPhone,
  FiUser,
  FiArrowLeftCircle,
  FiArrowRightCircle,
} from 'react-icons/fi';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { FaBirthdayCake } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../../hooks/useTranslation';
import getValidationErrors from '../../../utils/getValidationErrors';
import { config } from '../../../config';
import Input from '../../../components/Input';
import Button from '../../../components/Button';

import {
  Container,
  ContainerStep,
  Content,
  AnimationContainer,
  Title,
  ColInput,
} from './styles';
import Radio from '../../../components/Radio';
import {
  customerClear,
  customerStepRequest,
  updateCustomerCreate,
} from '../../../store/modules/auth/actions';
import {
  AuthActionStep,
  CustomerData,
  UserCreate,
} from '../../../store/modules/auth/types';
import SignUp from '..';
import { viewModal } from '../../../store/modules/parameter/actions';
import { StoreState } from '../../../store/createStore';
import SignUpStep02 from '../SignUpStep02';
import { bValidCpf } from '../../../utils/bValidCpf';
import { bValidBirthDay } from '../../../utils/bValidBirthDay';
import { isAdult } from '../../../utils/isAdult';

const SignUpStep01: React.FC = () => {
  const { onTranslation: t } = useTranslation();
  const formRef = useRef<FormHandles>(null);
  const dispatch = useDispatch();
  const { step, userCreate } = useSelector((state: StoreState) => state.auth);

  const handleSubmit = useCallback(async (data: CustomerData) => {
    try {
      formRef.current?.setErrors({});

      const schema = Yup.object().shape({
        cpf_cnpj: Yup.string().transform((value: string) => {
          return value.split(/\D/).join('')
        })
          .test({
            test: cpf_cnpj => bValidCpf(cpf_cnpj),
            message: t('main.messages_yup.cpf_invalid')
          }).test({
            test: birth => !isNaN(birth),
            message: t('main.messages_yup.write_only_number')
          }).required(t('main.messages_yup.cpf_required')),
        birth: Yup.string().transform((value: string) => {
          return value.split(/\D/).join('')
        })
          .test({
            test: birth => !isNaN(birth),
            message: t('main.messages_yup.write_only_number')
          }).test({
            test: birth => bValidBirthDay(birth),
            message: t('main.messages_yup.date_of_birth_invalid')
          })
          .min(6, `${t('main.messages_yup.min')} 6 ${t('main.messages_yup.digits')}`).max(8, `${t('main.messages_yup.max')} 8 ${t('main.messages_yup.digits')}`)
          .required(t('main.messages_yup.date_of_birth_required'))
          .test('is-adult', 'Você deve ter pelo menos 18 anos', (birth) => isAdult(birth as string)),
        phone_mobile: Yup.string().transform((value: string) => {
          return value.split(/\D/).join('')
        })
          .matches(/^\d{2}\d{4,5}\d{4}$/gi,
            {
              message: t('main.messages_yup.phone_invalid')
            })
          .required(t('main.messages_yup.phone_required')).test({
            test: phone_mobile => !isNaN(phone_mobile),
            message: t('main.messages_yup.write_only_number')
          }),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const preRequest = data.genre ? data.genre[0] : '';
      if (data.genre) data.genre = preRequest;
      //konsole.log('handleSubmit:', data);


      const customerData: UserCreate = {
        date_of_birth: data.birth?.split(/\D/).join(''),
        customer_id: data.cpf_cnpj?.split(/\D/).join(''),
        gender: data.genre,
        phone: data.phone_mobile?.split(/\D/).join(''),
      };

      dispatch(updateCustomerCreate(customerData, AuthActionStep.CST_STEP_03));
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRef.current?.setErrors(errors);
        //konsole.log('handleSubmit errors:', errors);
      }
    }
  }, [dispatch, t]);

  const handleStep = useCallback(() => {
    dispatch(customerStepRequest(AuthActionStep.CST_STEP_01));
    dispatch(viewModal(SignUp, { width: '50%', height: '80%' }));
  }, [dispatch]);

  useEffect(() => {
    if (step === 'CST_STEP_03') {
      dispatch(customerClear());
      dispatch(viewModal(SignUpStep02, { width: '50%', height: '80%' }));
    }
  }, [step, dispatch]);

  useEffect(() => {
    if (formRef.current && userCreate.customer_id) {
      const genderType: Record<string, string> = {
        "m": "male",
        "n": "nogenre",
        "o": "others",
        "f": "female"
      }
      formRef.current.setData({
        cpf_cnpj: userCreate.customer_id,
        birth: userCreate.date_of_birth,
        phone_mobile: userCreate.phone,
        genre: genderType[userCreate.gender || 'n']
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <Content>
        <AnimationContainer>
          <ContainerStep>
            <FiArrowLeftCircle
              size={36}
              color="var(--primary)"
              onClick={handleStep}
            />
            <img src={config.logo} alt="ConectoVarejo" />
            <FiArrowRightCircle size={36} color="var(--primary)" />
          </ContainerStep>

          <Form
            // initialData={{
            //   cpf_cnpj: '31010351805',
            //   birth: '1984-08-21',
            //   phone_mobile: '11989701516',
            // }}
            ref={formRef}
            onSubmit={handleSubmit}
          >
            <Title>
              <span>{t('main.modal_signup.signupstep01.more_about_you')}</span>
            </Title>

            <ColInput>
              <div style={{ marginBottom: '30px' }} />
            </ColInput>

            <ColInput>
              <Input
                icon={FiUser}
                name="cpf_cnpj"
                textLabel={t('main.modal_signup.signupstep01.input_cpf')}
                mask="###.###.###-##"
                formRef={formRef}
              />
            </ColInput>

            <ColInput>
              <Input
                icon={FaBirthdayCake}
                name="birth"
                textLabel={t('main.modal_signup.signupstep01.input_date_of_birth')}
                mask="##/##/####"
                formRef={formRef}
              />
            </ColInput>

            <ColInput>
              <Input
                icon={FiPhone}
                name="phone_mobile"
                textLabel={t('main.modal_signup.signupstep01.input_phone')}
                mask="(##) #####-####"
                formRef={formRef}
              />
            </ColInput>

            <ColInput>
              <Radio
                title={t('main.modal_signup.signupstep01.genre')}
                name="genre"
                options={[
                  { id: 'male', label: t('main.modal_signup.signupstep01.radio_male') },
                  { id: 'female', label: t('main.modal_signup.signupstep01.radio_female') },
                  { id: 'others', label: t('main.modal_signup.signupstep01.radio_others') },
                  { id: 'nogenre', label: t('main.modal_signup.signupstep01.radio_do_not_inform') },
                ]}
              />
            </ColInput>

            <ColInput>
              <Button
                button={{ type: 'submit' }}
                atributes={{
                  buttonType: 'primary',
                  textColor: '#fff',
                  textSize: '30px',
                  borderColor: '#dddddd',
                }}
              >
                 {t('main.modal_signup.button_signup')}
              </Button>
            </ColInput>
          </Form>
        </AnimationContainer>
      </Content>
    </Container>
  );
};

export default SignUpStep01;
